import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const EmpresaPage = () => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    document.title = 'Empresa | Pintores Profesionales Barcelona | PINTORDECO';
    
    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${siteUrl}/empresa`);
    }
    
    // Update Open Graph meta tags
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${siteUrl}/empresa`);
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Empresa | Pintores Profesionales Barcelona | PINTORDECO');
    }
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Conoce a PINTORDECO Barcelona: más de 15 años como pintores profesionales. Calidad, limpieza, puntualidad. Equipo experto en pintura interior y exterior.');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Conoce a PINTORDECO Barcelona: más de 15 años como pintores profesionales. Calidad, limpieza, puntualidad. Equipo experto en pintura interior y exterior.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'pintores Barcelona, empresa pintura Barcelona, pintores profesionales, equipo pintura Barcelona');
    }

    // Structured Data - AboutPage & Organization
    const scriptLD = document.createElement('script');
    scriptLD.type = 'application/ld+json';
    scriptLD.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "@id": `${siteUrl}/empresa#webpage`,
      "url": `${siteUrl}/empresa`,
      "name": "Empresa | PINTORDECO Barcelona",
      "description": "Conoce a PINTORDECO Barcelona: más de 15 años como pintores profesionales. Calidad, limpieza, puntualidad.",
      "inLanguage": "es-ES",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "PINTORDECO Barcelona"
      },
      "mainEntity": {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "PINTORDECO Barcelona",
        "url": siteUrl,
        "logo": `${siteUrl}/vite.svg`,
        "foundingDate": "2008",
        "description": "Empresa de pintura profesional en Barcelona con más de 15 años de experiencia",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Carrer de Sant Eloi, 2, B",
          "addressLocality": "Barcelona",
          "addressRegion": "Barcelona",
          "postalCode": "08038",
          "addressCountry": "ES"
        },
        "telephone": "+34690220944",
        "areaServed": [
          {
            "@type": "City",
            "name": "Barcelona"
          }
        ]
      }
    });
    document.head.appendChild(scriptLD);
    
    return () => {
      if (scriptLD.parentNode) {
        scriptLD.parentNode.removeChild(scriptLD);
      }
    };
  }, []);

  const valores = [
    {
      icon: 'ri-shield-check-line',
      titulo: 'Calidad garantizada',
      descripcion: 'Utilizamos materiales de primera calidad y técnicas profesionales para asegurar acabados perfectos y duraderos en cada proyecto.',
    },
    {
      icon: 'ri-time-line',
      titulo: 'Puntualidad',
      descripcion: 'Respetamos los plazos acordados y coordinamos horarios según tus necesidades y las normativas de tu comunidad.',
    },
    {
      icon: 'ri-brush-line',
      titulo: 'Limpieza impecable',
      descripcion: 'Protegemos suelos, muebles y zonas comunes. Al finalizar, dejamos tu espacio perfectamente limpio y listo para usar.',
    },
    {
      icon: 'ri-customer-service-line',
      titulo: 'Atención personalizada',
      descripcion: 'Asesoramiento profesional en materiales, colores y acabados. Te acompañamos en cada paso del proyecto.',
    },
  ];

  const equipo = [
    {
      nombre: 'Equipo oficial',
      avatar: 'https://public.readdy.ai/ai/img_res/36630322a039b548ddef0a4b11f3019e.jpg',
    },
    {
      nombre: 'Pintores expertos',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20painter%20portrait%20barcelona%20spain%20worker%20friendly%20uniform&width=100&height=100&seq=team-member-2&orientation=squarish',
    },
    {
      nombre: 'Especialistas',
      avatar: 'https://readdy.ai/api/search-image?query=painting%20specialist%20portrait%20barcelona%20spain%20worker%20friendly%20uniform&width=100&height=100&seq=team-member-3&orientation=squarish',
    },
    {
      nombre: 'Profesionales',
      avatar: 'https://readdy.ai/api/search-image?query=painting%20technician%20portrait%20barcelona%20spain%20worker%20friendly%20uniform&width=100&height=100&seq=team-member-4&orientation=squarish',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-primary/5 to-accent/5 overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-full lg:w-1/2 hidden lg:block">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20painting%20team%20working%20interior%20renovation%20barcelona%20painters%20uniform%20clean%20workspace&width=1200&height=800&seq=empresa-hero-1&orientation=landscape"
            alt="Equipo PINTORDECO Barcelona"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/60 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <nav className="text-sm text-gray-500 mb-6">
              <Link to="/" className="hover:text-accent cursor-pointer">Inicio</Link>
              <span className="mx-2">/</span>
              <span className="text-primary font-semibold">Empresa</span>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl lg:text-6xl font-bold text-primary mb-6 leading-tight">
                Pintores profesionales en Barcelona
              </h1>
              <p className="text-lg lg:text-xl text-gray-700 mb-6 leading-relaxed">
                Con más de 15 años de experiencia, somos tu empresa de confianza para todo tipo de <Link to="/servicios" className="text-gray-700 hover:text-accent transition-colors font-semibold underline">trabajos de pintura en Barcelona</Link> y área metropolitana.
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <span className="text-base lg:text-lg text-gray-700">Limpieza y orden en cada proyecto</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <span className="text-base lg:text-lg text-gray-700">Puntualidad y cumplimiento de plazos</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <span className="text-base lg:text-lg text-gray-700">Materiales de calidad profesional</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <span className="text-base lg:text-lg text-gray-700">Presupuestos detallados por escrito</span>
                </li>
              </ul>

              <a
                href="https://wa.me/34690220944"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center space-x-3 px-8 py-4 bg-accent text-white rounded-full font-semibold text-lg hover:bg-accent/90 transition-all cursor-pointer shadow-lg"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span>Contactar ahora</span>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sobre Nosotros */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <div className="text-sm text-gray-600 mb-4">/ Nuestra historia</div>
              <h2 className="text-3xl lg:text-5xl font-bold text-primary mb-6 leading-tight">
                Más de 15 años transformando espacios
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-7"
            >
              <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-6">
                PINTORDECO nació con una visión clara: ofrecer servicios de pintura profesional en Barcelona donde la calidad, la limpieza y el trato cercano sean prioritarios. Desde nuestros inicios, hemos trabajado en cientos de proyectos residenciales y comerciales, siempre con el mismo compromiso: dejar cada espacio impecable y a nuestros clientes completamente satisfechos. <Link to="/contacto" className="text-gray-700 hover:text-accent transition-colors underline">Solicita tu presupuesto gratuito</Link>.
              </p>
              <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                Entendemos que <Link to="/servicios" className="text-gray-600 hover:text-accent transition-colors underline">pintar</Link> no es solo aplicar color a las paredes. Es transformar espacios, crear ambientes, proteger superficies y, sobre todo, cuidar cada detalle para que el resultado final sea exactamente lo que esperabas. Por eso, cada miembro de nuestro equipo está formado en las últimas técnicas y comprometido con la excelencia. Si tienes dudas, consulta nuestras <Link to="/faq" className="text-gray-600 hover:text-accent transition-colors underline">preguntas frecuentes</Link>.
              </p>
            </motion.div>
          </div>

          <div className="mt-12 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-center space-x-4">
              <div className="flex -space-x-3">
                {equipo.map((miembro, index) => (
                  <div
                    key={index}
                    className="w-12 h-12 lg:w-14 lg:h-14 rounded-full border-4 border-white overflow-hidden flex-shrink-0"
                  >
                    <img
                      src={miembro.avatar}
                      alt={miembro.nombre}
                      className="w-full h-full object-cover"
                      style={{ filter: 'sepia(0.3)' }}
                    />
                  </div>
                ))}
              </div>
              <p className="text-sm lg:text-base text-gray-700 font-medium">Nuestro equipo de pintores profesionales</p>
            </div>

            <div className="text-gray-600">
              <span className="text-sm">• Barcelona y área metropolitana</span>
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-primary mb-4">
              Nuestros valores
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Los principios que guían nuestro trabajo como <Link to="/" className="text-gray-600 hover:text-accent transition-colors underline">pintores profesionales en Barcelona</Link>
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {valores.map((valor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-accent/30 transition-all"
              >
                <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4">
                  <i className={`${valor.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-lg lg:text-xl font-bold text-primary mb-3">{valor.titulo}</h3>
                <p className="text-sm lg:text-base text-gray-600 leading-relaxed">{valor.descripcion}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proceso Detallado con Timeline Vertical */}
      <section className="py-20 bg-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-primary mb-4">
              Cómo trabajamos
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Un proceso probado que garantiza resultados excepcionales en cada <Link to="/servicios" className="text-gray-600 hover:text-accent transition-colors underline">proyecto de pintura</Link>
            </p>
          </motion.div>

          <div className="relative">
            {/* Línea vertical */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-primary/20"></div>

            {/* Paso 1 */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative mb-16 lg:grid lg:grid-cols-2 lg:gap-12 items-center"
            >
              <div className="lg:text-right mb-6 lg:mb-0">
                <div className="inline-block lg:float-right bg-white rounded-2xl p-6 shadow-lg max-w-md w-full">
                  <div className="text-4xl lg:text-5xl font-bold text-accent mb-3">01</div>
                  <h3 className="text-xl lg:text-2xl font-bold text-primary mb-3">Valoración inicial</h3>
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                    Nos envías fotos o concertamos una visita para ver el estado actual, medir los metros cuadrados y entender tus necesidades específicas. Te preguntamos por colores, acabados y plazos. <Link to="/faq" className="text-gray-600 hover:text-accent transition-colors underline">¿Dudas sobre el proceso?</Link>
                  </p>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="w-full h-64 overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=painter%20measuring%20wall%20interior%20space%20clipboard%20barcelona%20apartment%20work%20uniform&width=600&height=400&seq=proceso-valoracion-1&orientation=landscape"
                    alt="Valoración de proyecto"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-accent rounded-full border-4 border-white shadow-lg z-10"></div>
            </motion.div>

            {/* Paso 2 */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative mb-16 lg:grid lg:grid-cols-2 lg:gap-12 items-center"
            >
              <div className="hidden lg:block">
                <div className="w-full h-64 overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=detailed%20painting%20quote%20document%20estimate%20paper%20pen%20calculator%20budget%20desk&width=600&height=400&seq=proceso-presupuesto-1&orientation=landscape"
                    alt="Presupuesto detallado"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="mb-6 lg:mb-0">
                <div className="bg-white rounded-2xl p-6 shadow-lg max-w-md w-full">
                  <div className="text-4xl lg:text-5xl font-bold text-accent mb-3">02</div>
                  <h3 className="text-xl lg:text-2xl font-bold text-primary mb-3">Presupuesto detallado</h3>
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                    Te enviamos un presupuesto por escrito con todos los detalles: superficie a pintar, materiales a utilizar, plazos estimados y precio final. Sin sorpresas ni costes ocultos.
                  </p>
                </div>
              </div>
              <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-accent rounded-full border-4 border-white shadow-lg z-10"></div>
            </motion.div>

            {/* Paso 3 */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative mb-16 lg:grid lg:grid-cols-2 lg:gap-12 items-center"
            >
              <div className="lg:text-right mb-6 lg:mb-0">
                <div className="inline-block lg:float-right bg-white rounded-2xl p-6 shadow-lg max-w-md w-full">
                  <div className="text-4xl lg:text-5xl font-bold text-accent mb-3">03</div>
                  <h3 className="text-xl lg:text-2xl font-bold text-primary mb-3">Preparación y protección</h3>
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                    Protegemos suelos, muebles y elementos que no se pintan. Reparamos grietas, lijamos, masillamos y preparamos todas las superficies. La preparación es clave para un acabado perfecto.
                  </p>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="w-full h-64 overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=painter%20protecting%20floor%20furniture%20plastic%20covers%20masking%20tape%20barcelona%20apartment&width=600&height=400&seq=proceso-preparacion-1&orientation=landscape"
                    alt="Preparación del espacio"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-accent rounded-full border-4 border-white shadow-lg z-10"></div>
            </motion.div>

            {/* Paso 4 */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative mb-16 lg:grid lg:grid-cols-2 lg:gap-12 items-center"
            >
              <div className="hidden lg:block">
                <div className="w-full h-64 overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20painter%20painting%20wall%20roller%20interior%20white%20paint%20barcelona%20apartment&width=600&height=400&seq=proceso-pintura-1&orientation=landscape"
                    alt="Aplicación de pintura"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="mb-6 lg:mb-0">
                <div className="bg-white rounded-2xl p-6 shadow-lg max-w-md w-full">
                  <div className="text-4xl lg:text-5xl font-bold text-accent mb-3">04</div>
                  <h3 className="text-xl lg:text-2xl font-bold text-primary mb-3">Aplicación de pintura</h3>
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                    Aplicamos las capas necesarias según el tipo de superficie y acabado. Trabajamos con orden, respetando horarios y manteniendo el espacio limpio durante todo el proceso.
                  </p>
                </div>
              </div>
              <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-accent rounded-full border-4 border-white shadow-lg z-10"></div>
            </motion.div>

            {/* Paso 5 */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative lg:grid lg:grid-cols-2 lg:gap-12 items-center"
            >
              <div className="lg:text-right mb-6 lg:mb-0">
                <div className="inline-block lg:float-right bg-white rounded-2xl p-6 shadow-lg max-w-md w-full">
                  <div className="text-4xl lg:text-5xl font-bold text-accent mb-3">05</div>
                  <h3 className="text-xl lg:text-2xl font-bold text-primary mb-3">Revisión y entrega</h3>
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                    Retiramos todas las protecciones, limpiamos a fondo y hacemos una revisión final contigo. No damos por terminado el trabajo hasta que estés completamente satisfecho con el resultado.
                  </p>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="w-full h-64 overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=clean%20finished%20painted%20interior%20white%20walls%20modern%20apartment%20barcelona%20perfect%20result&width=600&height=400&seq=proceso-entrega-1&orientation=landscape"
                    alt="Entrega del proyecto"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-accent rounded-full border-4 border-white shadow-lg z-10"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
              ¿Hablamos de tu proyecto?
            </h2>
            <p className="text-lg lg:text-xl text-white/90 mb-8 max-w-3xl mx-auto px-4">
              Cuéntanos qué necesitas y te enviaremos un <Link to="/contacto" className="text-white hover:text-accent transition-colors underline">presupuesto personalizado</Link> sin compromiso
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+34690220944"
                className="whitespace-nowrap inline-flex items-center justify-center space-x-3 px-8 py-4 bg-white text-primary rounded-full font-semibold text-lg hover:bg-gray-100 transition-all cursor-pointer shadow-lg"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>690 22 09 44</span>
              </a>
              <Link
                to="/contacto"
                className="whitespace-nowrap inline-flex items-center justify-center space-x-3 px-8 py-4 bg-accent text-white rounded-full font-semibold text-lg hover:bg-accent/90 transition-all cursor-pointer shadow-lg"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>Solicitar presupuesto</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EmpresaPage;
